/**
 * Player tournament drill-down: league → campaign → season (full-width “active tournaments” flow).
 */
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import {
  FaChevronLeft,
  FaSearch,
  FaCalendarAlt,
  FaArrowRight,
  FaCheckCircle,
} from 'react-icons/fa';
import { publicAssetUrl } from '../../utils/publicAssetUrl';
import { isHelveticPadelSeriesTenant, pickPrimaryRegistrationSeasonId, resolveClosedRegistrationNotice } from '../../utils/tournamentRegistrationHelpers';
import RegistrationCampaignPanel from './RegistrationCampaignPanel';

const BRAND = { blue: '#043663', red: '#D52B1E', teal: '#0f766e' };

const PageWrap = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 0 2rem;
`;

const StepSection = styled.section`
  margin-bottom: 2.5rem;
`;

const StepHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
`;

const StepTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.15rem, 2.8vw, 1.5rem);
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.03em;
`;

const OpenRegBadge = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  background: ${BRAND.red};
  color: #fff;
  white-space: nowrap;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.95rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${BRAND.blue};
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const Subtitle = styled.p`
  margin: 0 0 1.35rem;
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.55;
  max-width: 52rem;
  width: 100%;
`;

const SearchWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 340px;
  margin-bottom: 1.1rem;
  .icon {
    position: absolute;
    left: 0.85rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    pointer-events: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.55rem 0.85rem 0.55rem 2.35rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  font-size: 0.9rem;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.08);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.25rem;
  width: 100%;
`;

const Card = styled.article`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 24px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    transform 0.18s ease,
    border-color 0.18s ease;
  &:hover {
    box-shadow:
      0 4px 12px rgba(15, 23, 42, 0.08),
      0 16px 40px rgba(4, 54, 99, 0.1);
    transform: translateY(-3px);
    border-color: rgba(4, 54, 99, 0.12);
  }
  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }
`;

const TenantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 1.35rem;
  width: 100%;
`;

const TenantLeagueCard = styled(Card)`
  border-width: 2px;
  border-style: solid;
  border-color: ${(p) => (p.$selected ? BRAND.red : 'rgba(15, 23, 42, 0.08)')};
  box-shadow: ${(p) =>
    p.$selected
      ? '0 16px 40px rgba(213, 43, 30, 0.12)'
      : '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)'};
  transform: none;
  &:hover {
    transform: translateY(-2px);
    border-color: ${(p) => (p.$selected ? BRAND.red : 'rgba(4, 54, 99, 0.18)')};
    box-shadow: ${(p) =>
      p.$selected
        ? '0 20px 48px rgba(213, 43, 30, 0.16)'
        : '0 4px 12px rgba(15, 23, 42, 0.08), 0 16px 40px rgba(4, 54, 99, 0.1)'};
  }
`;

const SelectLeagueCue = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${BRAND.red};
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const CardBanner = styled.div`
  position: relative;
  height: 108px;
  background: linear-gradient(135deg, #0c4a6e 0%, ${BRAND.blue} 52%, #152a45 100%);
  overflow: hidden;
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TenantBanner = styled.div`
  position: relative;
  height: 120px;
  background: linear-gradient(145deg, #1e3a5f 0%, ${BRAND.blue} 45%, #0c2744 100%);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;

  img {
    display: block;
    width: auto;
    height: auto;
    max-width: 92%;
    max-height: 100%;
    object-fit: contain;
    object-position: center;
  }
`;

const TenantInitials = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.25rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.04em;
  z-index: 0;
`;

const CardBody = styled.div`
  padding: 1rem 1.1rem 1.1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const CardTitle = styled.div`
  font-weight: 700;
  font-size: 1.05rem;
  color: #0f172a;
  line-height: 1.35;
`;

const CardDesc = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  margin-top: 0.5rem;
  font-size: 0.78rem;
  color: #94a3b8;
  line-height: 1.4;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.65rem;
`;

const Chip = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  letter-spacing: 0.02em;
  background: #f1f5f9;
  color: #475569;
  ${(p) =>
    p.$tone === 'live' &&
    `
    background: #dcfce7;
    color: #166534;
  `}
  ${(p) =>
    p.$tone === 'muted' &&
    `
    background: #f8fafc;
    color: #64748b;
  `}
  ${(p) =>
    p.$tone === 'done' &&
    `
    background: #e2e8f0;
    color: #334155;
  `}
  ${(p) =>
    p.$tone === 'accent' &&
    `
    background: rgba(4, 54, 99, 0.08);
    color: ${BRAND.blue};
  `}
`;

const CardAction = styled.div`
  margin-top: auto;
  padding-top: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${BRAND.teal};
`;

const ActionHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const EmptyHint = styled.p`
  margin: 0;
  padding: 2.5rem 1.25rem;
  text-align: center;
  color: #64748b;
  font-size: 0.9375rem;
  line-height: 1.55;
  background: #fff;
  border-radius: 16px;
  border: 1px dashed #e2e8f0;
`;

function initials(name) {
  if (!name || typeof name !== 'string') return '?';
  const p = name.trim().split(/\s+/);
  if (p.length >= 2) return (p[0][0] + p[p.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

function formatDateShort(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

function seasonPhaseChip(status, t) {
  const s = String(status || '').toLowerCase();
  const p = 'phase2.playerDashboard.playerTournamentFlow';
  if (s === 'active') return { label: t(`${p}.seasonPhaseLive`), tone: 'live' };
  if (s === 'completed') return { label: t(`${p}.seasonPhaseDone`), tone: 'done' };
  if (s === 'draft' || s === 'upcoming') return { label: t(`${p}.seasonPhaseUpcoming`), tone: 'muted' };
  if (s.includes('registration')) return { label: t(`${p}.seasonPhaseRegistration`), tone: 'accent' };
  return { label: status ? String(status).replace(/_/g, ' ') : t(`${p}.seasonPhaseFallback`), tone: 'muted' };
}

const NoCampaignCard = styled.div`
  border-radius: 16px;
  border: 2px solid rgba(4, 54, 99, 0.14);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 45%, #fff5f4 100%);
  padding: 1.75rem 1.5rem;
  text-align: center;
  box-shadow: 0 8px 28px rgba(4, 54, 99, 0.08);
  max-width: 42rem;
  margin: 0 auto 0 0;
`;

const NoCampaignTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.15rem;
  font-weight: 800;
  color: #043663;
  letter-spacing: -0.02em;
`;

const NoCampaignBody = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.55;
  white-space: pre-wrap;
`;

export default function TournamentFlowPickers({
  step,
  tenantSearch,
  setTenantSearch,
  tenantsList,
  tenantId = '',
  campaigns,
  registrationEligibleCampaigns = [],
  seasonsList = [],
  selectedCampaign,
  campaignSeasons,
  selectedTenant,
  setParams,
  campaignsLoading,
  campaignCompleted = false,
  campaignsOnlyMode = false,
  heroTeamPresenceLoading = false,
  heroRegisterHref = '',
}) {
  const { t } = useTranslation();
  const tf = 'phase2.playerDashboard.playerTournamentFlow';

  const filteredTenants = useMemo(() => {
    if (!tenantSearch.trim()) return tenantsList;
    const q = tenantSearch.trim().toLowerCase();
    return tenantsList.filter(
      (tenantRow) =>
        (tenantRow.name && tenantRow.name.toLowerCase().includes(q)) ||
        (tenantRow.slug && tenantRow.slug.toLowerCase().includes(q)) ||
        (tenantRow.country_code && tenantRow.country_code.toLowerCase().includes(q))
    );
  }, [tenantsList, tenantSearch]);

  const clearBelowTenant = {
    campaign_id: '',
    season_id: '',
    team_id: '',
    view: '',
    register: '',
    use_existing: '',
    skip_auto_season: '',
  };
  const clearBelowCampaign = {
    season_id: '',
    team_id: '',
    view: '',
    register: '',
    use_existing: '',
    skip_auto_season: '',
  };

  const pickTenant = (t) =>
    setParams({
      tenant_id: t.id,
      season_id: '',
      team_id: '',
      view: '',
      campaign_id: '',
      register: '',
      use_existing: '',
      skip_auto_season: '',
    });

  const pickSeason = (seasonId) => {
    setParams({
      season_id: seasonId,
      team_id: '',
      view: '',
      register: '',
      use_existing: '',
      skip_auto_season: '',
    });
  };

  const renderLeagueStep = (selectedId) => (
    <StepSection aria-label={t(`${tf}.step1Aria`)}>
      <StepHeaderRow>
        <StepTitle>{t(`${tf}.step1Title`)}</StepTitle>
      </StepHeaderRow>
      <Subtitle>{t(`${tf}.step1Subtitle`)}</Subtitle>
      <SearchWrap>
        <FaSearch className="icon" size={14} />
        <SearchInput
          type="search"
          placeholder={t(`${tf}.searchPlaceholder`)}
          value={tenantSearch}
          onChange={(e) => setTenantSearch(e.target.value)}
          aria-label={t(`${tf}.searchAria`)}
        />
      </SearchWrap>
      {filteredTenants.length === 0 ? (
        <EmptyHint>{t(`${tf}.noLeaguesMatch`)}</EmptyHint>
      ) : (
        <TenantGrid>
          {filteredTenants.map((tenantRow) => {
            const logoSrc = tenantRow.tournament_logo_url ? publicAssetUrl(tenantRow.tournament_logo_url) : null;
            const rowSel = selectedId && String(selectedId) === String(tenantRow.id);
            const blurb = isHelveticPadelSeriesTenant(tenantRow)
              ? t(`${tf}.leagueHelveticBlurb`)
              : tenantRow.description ||
                (tenantRow.slug
                  ? `${String(tenantRow.slug).replace(/-/g, ' ')} · ${t(`${tf}.leagueDefaultBlurb`)}`
                  : null) ||
                (tenantRow.country_code
                  ? t(`${tf}.leagueCountryFootprint`, { country: tenantRow.country_code })
                  : t(`${tf}.leagueGenericBlurb`));
            return (
              <TenantLeagueCard
                key={tenantRow.id}
                $selected={rowSel}
                role="button"
                tabIndex={0}
                onClick={() => pickTenant(tenantRow)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    pickTenant(tenantRow);
                  }
                }}
              >
                <TenantBanner>
                  {logoSrc ? (
                    <img src={logoSrc} alt="" />
                  ) : (
                    <TenantInitials aria-hidden>{initials(tenantRow.name || tenantRow.slug)}</TenantInitials>
                  )}
                </TenantBanner>
                <CardBody>
                  <CardTitle>{tenantRow.name || t(`${tf}.leagueFallback`)}</CardTitle>
                  <CardDesc style={{ WebkitLineClamp: 3 }}>{blurb}</CardDesc>
                  <SelectLeagueCue>
                    {rowSel ? (
                      <>
                        <FaCheckCircle size={14} aria-hidden />
                        {t(`${tf}.leagueSelected`)}
                      </>
                    ) : (
                      <>
                        {t(`${tf}.selectLeague`)}
                        <FaArrowRight size={10} aria-hidden />
                      </>
                    )}
                  </SelectLeagueCue>
                </CardBody>
              </TenantLeagueCard>
            );
          })}
        </TenantGrid>
      )}
    </StepSection>
  );

  if (step === 'tenants') {
    return <PageWrap>{renderLeagueStep(null)}</PageWrap>;
  }

  if (step === 'campaigns') {
    const regHero = registrationEligibleCampaigns[0] || null;
    const openRegCount = registrationEligibleCampaigns.length;
    const regSeasonId = regHero ? pickPrimaryRegistrationSeasonId(regHero, seasonsList) : '';
    const fromList = regSeasonId ? seasonsList.find((s) => String(s.id) === String(regSeasonId)) : null;
    const fromCampaignRow = regHero
      ? (regHero.seasons || []).find((s) => String(s.season_id || s.id) === String(regSeasonId))
      : null;
    const seasonLabel =
      fromList?.name || fromCampaignRow?.season_name || fromCampaignRow?.name || t(`${tf}.seasonLabelFallback`);
    const closedNotice = !regHero ? resolveClosedRegistrationNotice(campaigns) : null;
    const closedTitle =
      closedNotice?.title || t(`${tf}.noActiveCampaignTitle`);
    const closedBody =
      closedNotice?.body || t(`${tf}.noActiveCampaignBody`);

    return (
      <PageWrap>
        <Toolbar style={{ marginBottom: '1rem' }}>
          <BackBtn
            type="button"
            onClick={() => setParams({ tenant_id: '', ...clearBelowTenant })}
            aria-label={t(`${tf}.backAllLeaguesAria`)}
          >
            <FaChevronLeft /> {t(`${tf}.backAllLeagues`)}
          </BackBtn>
        </Toolbar>
        {renderLeagueStep(selectedTenant?.id)}
        <StepSection aria-label={t(`${tf}.step2Aria`)}>
          <StepHeaderRow>
            <StepTitle>{t(`${tf}.step2Title`)}</StepTitle>
            {openRegCount > 0 ? <OpenRegBadge>{t(`${tf}.badgeOpen`)}</OpenRegBadge> : null}
          </StepHeaderRow>
          <Subtitle>
            {selectedTenant?.name ? (
              <Trans
                i18nKey={`${tf}.step2SubtitleWithLeague`}
                values={{ name: selectedTenant.name }}
                components={{ 0: <strong style={{ color: '#0f172a' }} /> }}
              />
            ) : (
              t(`${tf}.step2SubtitleNoLeague`)
            )}
          </Subtitle>
          {campaignsLoading ? (
            <EmptyHint>{t(`${tf}.loadingCampaigns`)}</EmptyHint>
          ) : campaigns.length === 0 ? (
            <EmptyHint>
              {campaignsOnlyMode ? (
                <Trans i18nKey={`${tf}.noPublishedCampaignsMode`} components={{ 0: <strong /> }} />
              ) : (
                t(`${tf}.noPublishedCampaigns`)
              )}
            </EmptyHint>
          ) : !regHero ? (
            <NoCampaignCard>
              <NoCampaignTitle>{closedTitle}</NoCampaignTitle>
              <NoCampaignBody>{closedBody}</NoCampaignBody>
            </NoCampaignCard>
          ) : (
            <RegistrationCampaignPanel
              campaign={regHero}
              tenantName={selectedTenant?.name}
              seasonLabel={seasonLabel}
              presenceLoading={heroTeamPresenceLoading}
              registerHref={heroRegisterHref || undefined}
              includeBanner
            />
          )}
        </StepSection>
      </PageWrap>
    );
  }

  if (step === 'seasons') {
    return (
      <PageWrap>
        <Toolbar style={{ marginBottom: '1rem' }}>
          <BackBtn
            type="button"
            onClick={() =>
              setParams(
                campaignCompleted
                  ? {
                      campaign_id: '',
                      season_id: '',
                      team_id: '',
                      view: '',
                      register: '',
                      use_existing: '',
                      skip_auto_season: '',
                    }
                  : { campaign_id: '', ...clearBelowCampaign }
              )
            }
            aria-label={t(`${tf}.step3BackAria`)}
          >
            <FaChevronLeft />{' '}
            {campaignCompleted ? t(`${tf}.step3BackAllCampaigns`) : t(`${tf}.step3BackCampaigns`)}
          </BackBtn>
        </Toolbar>
        {renderLeagueStep(selectedTenant?.id)}
        <StepSection>
          <StepHeaderRow>
            <StepTitle>{t(`${tf}.step3Title`)}</StepTitle>
          </StepHeaderRow>
          <Subtitle>
            {selectedCampaign?.title ? (
              <Trans
                i18nKey={`${tf}.step3SubtitleWithCampaign`}
                values={{ title: selectedCampaign.title }}
                components={{ 0: <strong style={{ color: '#0f172a' }} /> }}
              />
            ) : (
              t(`${tf}.step3PickSeason`)
            )}
          </Subtitle>
          {campaignSeasons.length === 0 ? (
            <EmptyHint>{t(`${tf}.noSeasonsLinked`)}</EmptyHint>
          ) : (
            <Grid>
              {campaignSeasons.map((s) => {
                const ph = seasonPhaseChip(s.status, t);
                return (
                  <Card
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => pickSeason(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        pickSeason(s.id);
                      }
                    }}
                  >
                    <CardBanner style={{ height: 72 }}>
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(255,255,255,0.9)',
                          fontSize: '1.25rem',
                          fontWeight: 800,
                        }}
                      >
                        <FaCalendarAlt style={{ opacity: 0.35, position: 'absolute' }} size={36} />
                        <span style={{ position: 'relative', zIndex: 1 }}>{initials(s.name)}</span>
                      </div>
                    </CardBanner>
                    <CardBody>
                      <CardTitle>{s.name}</CardTitle>
                      {(s.start_date || s.end_date) && (
                        <CardMeta>
                          {formatDateShort(s.start_date)} – {formatDateShort(s.end_date)}
                        </CardMeta>
                      )}
                      <ChipRow>
                        <Chip $tone={ph.tone === 'live' ? 'live' : ph.tone === 'done' ? 'done' : 'accent'}>
                          {ph.tone === 'live' ? <FaCheckCircle size={10} style={{ marginRight: 4 }} /> : null}
                          {ph.label}
                        </Chip>
                      </ChipRow>
                      <CardAction>
                        <ActionHint>
                          {t(`${tf}.selectSeason`)} <FaArrowRight size={11} />
                        </ActionHint>
                      </CardAction>
                    </CardBody>
                  </Card>
                );
              })}
            </Grid>
          )}
        </StepSection>
      </PageWrap>
    );
  }

  return null;
}
